import _ from 'lodash';
import { isUserAdminTier } from '../../../../shared/services/checkUserTierService';
import * as ActionTypes from '../../../constants/LicensesTableActionTypes';
import { PAGE_MODE } from '../../../constants/pageModeTypes';
import supplementalRoles from '../../../../../constants/supplemental-Roles';

const DEFAULT_USER_FIELDS = {
  Email: '',
  Name: '',
  RoleDisplayName: '',
  Orgs: [],
  OrgUnits: [],
  AllowedOhts: [],
  Actions: ActionTypes.ASSIGN
};

export const getAssignedUserLicenseInformation = (
  users,
  orgUnitUsers,
  pageMode,
  currentUserTier,
  isFreeLicenseType
) => {
  const userLicenseInfoResult = {
    organizationOrgUnitUsers: [],
    users: []
  };

  userLicenseInfoResult.users = users.map(user =>
    getFieldsByTier(user, pageMode, currentUserTier, isFreeLicenseType)
  );

  if (orgUnitUsers?.length) {
    userLicenseInfoResult.organizationOrgUnitUsers = processOrgUnitUsers(
      orgUnitUsers,
      currentUserTier,
      isFreeLicenseType
    );
  }

  return userLicenseInfoResult;
};

export const getUserLicenseInformation = (
  users,
  orgUnitUsers,
  licenseCount,
  assignedLicenseCount,
  orgUnits,
  pageMode,
  orgUnitId,
  currentUserTier,
  isFreeLicenseType
) => {
  const assignedUsersInfo = getAssignedUserLicenseInformation(
    users,
    orgUnitUsers,
    pageMode,
    currentUserTier,
    isFreeLicenseType
  );

  const userLicenseInfoResult = {
    assignedOrganizationOrgUnitLicenseCount: 0,
    assignedLicenseCount: assignedLicenseCount,
    licenseCount: licenseCount,
    organizationOrgUnitUsers: assignedUsersInfo.organizationOrgUnitUsers,
    users: assignedUsersInfo.users
  };

  const unassignedLicensesCount = licenseCount - assignedLicenseCount;
  if (unassignedLicensesCount > 0) {
    userLicenseInfoResult.users = [
      ...userLicenseInfoResult.users,
      ...createEmptyRows(unassignedLicensesCount, orgUnitId)
    ];
  }

  if (orgUnits?.length) {
    processOrgUnitsLicenseInfo(userLicenseInfoResult, orgUnits);
  }

  return userLicenseInfoResult;
};

export const getLicenseCount = (orgLicenseCount, orgUnitLicenseCount) => {
  if (orgLicenseCount != null) {
    return orgLicenseCount;
  }

  if (orgUnitLicenseCount != null) {
    return orgUnitLicenseCount;
  }

  return 0;
};

const adjustFieldDataWithAllText = values => (values?.length ? values : ['All']);

const processOrgUnitUsers = (orgUnitUsers, currentUserTier, isFreeLicenseType) => {
  return orgUnitUsers.map(user => ({
    ...user,
    RoleDisplayName: user.SystemRole?.DisplayName,
    Actions: getActionType(user, currentUserTier, isFreeLicenseType)
  }));
};

const processOrgUnitsLicenseInfo = (result, orgUnits) => {
  const orgUnitEmptyRows = createOrgUnitEmptyRows(orgUnits);
  result.organizationOrgUnitUsers = [...result.organizationOrgUnitUsers, ...orgUnitEmptyRows];
  result.assignedOrganizationOrgUnitLicenseCount = _(orgUnits).sumBy(x => x.licenseAssigned);
  result.orgUnitLicenseCount = _(orgUnits).sumBy(x => x.licenseTotal);
};

const getFieldsByTier = (obj, pageMode, currentUserTier, isFreeLicenseType) => {
  const defaultFields = {
    ...obj,
    RoleDisplayName: obj.SystemRole?.DisplayName,
    Actions: getActionType(obj, currentUserTier, isFreeLicenseType)
  };
  let fields = {};

  if (pageMode == PAGE_MODE.FDAVipOrg) {
    fields = {
      Orgs: adjustFieldDataWithAllText(obj.AllowedOrgs),
      OrgUnits: adjustFieldDataWithAllText(obj.AllowedOrgUnits),
      AllowedOhts: adjustFieldDataWithAllText(obj.AllowedOhts)
    };
  }
  if (pageMode === PAGE_MODE.MddapOrg) {
    fields = {
      OrgUnits: adjustFieldDataWithAllText(obj.AllowedOrgUnits)
    };
  }

  return { ...defaultFields, ...fields };
};

const getActionType = (userLicense, currentUserTier, isFreeLicenseType) => {
  if (userLicense.IsSuperUser && !isUserAdminTier(currentUserTier)) {
    return ActionTypes.NO_ACTION;
  }

  if (
    userLicense.SupplementalRoleId === supplementalRoles.ATM ||
    userLicense.SupplementalRoleId === supplementalRoles.ATL
  ) {
    return ActionTypes.VIEW;
  }

  return isFreeLicenseType ? ActionTypes.REMOVE : ActionTypes.EDIT;
};

const createEmptyRows = (count, orgUnitId) => {
  return Array.from({ length: count }, (_, i) => ({
    ...DEFAULT_USER_FIELDS,
    LicensedOrgUnitId: orgUnitId || null,
    key: `unassigned-license-${i + 1}`
  }));
};

const createOrgUnitEmptyRows = orgUnits => {
  return orgUnits.flatMap(unit => {
    const unassignedCount = unit.licenseTotal - unit.licenseAssigned;
    if (unassignedCount == 0) return [];

    return Array.from({ length: unassignedCount }, (_, i) => ({
      ...DEFAULT_USER_FIELDS,
      LicensedOrgUnit: unit.OrgUnitName,
      LicensedOrgUnitId: unit.OrgUnitId,
      key: `${unit.OrgUnitId}-unassigned-license-${i}`
    }));
  });
};
